/**
 * Phone number utilities for E.164 format normalization
 *
 * E.164 is the international phone number standard format: +[country code][number]
 * Example: +4917012345678 (German mobile number)
 */
/**
 * European countries with their dialing codes (ISO 3166-1 alpha-2 to E.164 country codes)
 */
export const COUNTRY_DIAL_CODES = {
    de: '+49', // Germany
    at: '+43', // Austria
    ch: '+41', // Switzerland
    be: '+32', // Belgium
    dk: '+45', // Denmark
    fi: '+358', // Finland
    fr: '+33', // France
    it: '+39', // Italy
    nl: '+31', // Netherlands
    no: '+47', // Norway
    pl: '+48', // Poland
    se: '+46', // Sweden
    es: '+34', // Spain
    cz: '+420', // Czech Republic
    gb: '+44', // United Kingdom
};
/**
 * Normalizes a phone number to E.164 format
 *
 * Handles various input formats:
 * - National format: 0170 1234567 -> +491701234567
 * - International without +: 49170 1234567 -> +491701234567
 * - Already E.164: +49170 1234567 -> +491701234567
 * - With country prefix: 00491701234567 -> +491701234567
 * - Auto-detects country code: +43 664 1234567 -> +436641234567 (Austrian)
 *
 * @param phone - The phone number to normalize (can include spaces, hyphens, parentheses)
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'de', 'at', 'ch'). Defaults to 'de'.
 *                      Used only if no country code is detected in the phone number.
 * @returns The normalized phone number in E.164 format, or null if invalid
 *
 * @example
 * normalizePhoneToE164('0170 1234567', 'de')     // '+491701234567'
 * normalizePhoneToE164('+43 664 1234567', 'de')  // '+436641234567' (auto-detects Austria)
 * normalizePhoneToE164('+41 79 123 4567', 'de')  // '+41791234567' (auto-detects Switzerland)
 * normalizePhoneToE164('1234', 'de')             // null (too short)
 */
export function normalizePhoneToE164(phone, countryCode = 'de') {
    if (!phone || !phone.trim()) {
        return null;
    }
    // Strip all non-numeric characters
    let cleanedPhone = phone.replace(/\D/g, '');
    if (!cleanedPhone) {
        return null;
    }
    // Handle international prefix '00' if present (e.g., 00491234... -> 491234...)
    if (cleanedPhone.startsWith('00')) {
        cleanedPhone = cleanedPhone.substring(2);
    }
    // Auto-detect country code from the phone number itself
    // Try to match against all known country codes (longest first to avoid matching prefixes like 4 vs 49)
    const sortedDialCodes = Object.entries(COUNTRY_DIAL_CODES)
        .map(([code, dialCode]) => ({ code, dialCode, digits: dialCode.replace(/\D/g, '') }))
        .sort((a, b) => b.digits.length - a.digits.length); // Sort by length descending
    let detectedDialCode = null;
    // Try to detect country code from the cleaned phone number
    for (const { dialCode, digits } of sortedDialCodes) {
        if (cleanedPhone.startsWith(digits)) {
            detectedDialCode = dialCode;
            cleanedPhone = cleanedPhone.substring(digits.length);
            break;
        }
    }
    // If no country code was detected, use the provided default
    if (!detectedDialCode) {
        const dialCode = COUNTRY_DIAL_CODES[countryCode.toLowerCase()];
        if (!dialCode) {
            // Unknown country code, default to Germany
            return normalizePhoneToE164(phone, 'de');
        }
        detectedDialCode = dialCode;
        // Remove leading zero (national format: 0170... -> 170...)
        if (cleanedPhone.startsWith('0')) {
            cleanedPhone = cleanedPhone.substring(1);
        }
    }
    // Validation: phone number should have at least 6 digits after country code
    // Most countries require 6-15 digits (ITU-T E.164 standard)
    if (cleanedPhone.length < 6 || cleanedPhone.length > 15) {
        return null;
    }
    // Return in E.164 format
    return `${detectedDialCode}${cleanedPhone}`;
}
/**
 * Validates if a phone number can be normalized to E.164 format
 *
 * @param phone - The phone number to validate
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns true if the phone can be normalized to valid E.164 format
 *
 * @example
 * isValidPhoneNumber('0170 1234567', 'de')  // true
 * isValidPhoneNumber('123', 'de')  // false (too short)
 */
export function isValidPhoneNumber(phone, countryCode = 'de') {
    return normalizePhoneToE164(phone, countryCode) !== null;
}
/**
 * Checks if a phone number is already in E.164 format
 *
 * @param phone - The phone number to check
 * @returns true if the phone is in E.164 format (+[1-15 digits])
 *
 * @example
 * isE164Format('+491701234567')  // true
 * isE164Format('0170 1234567')   // false
 */
export function isE164Format(phone) {
    if (!phone) {
        return false;
    }
    // E.164 format: + followed by 1-15 digits
    return /^\+\d{1,15}$/.test(phone.replace(/\s/g, ''));
}
/**
 * Formats an E.164 phone number for display
 *
 * @param phone - Phone number in E.164 format
 * @param format - Display format: 'international' or 'national'
 * @returns Formatted phone number, or the original if not E.164
 *
 * @example
 * formatPhoneForDisplay('+491701234567', 'international')  // '+49 170 1234567'
 * formatPhoneForDisplay('+491701234567', 'national')       // '0170 1234567'
 */
export function formatPhoneForDisplay(phone, format = 'international') {
    if (!phone || !isE164Format(phone)) {
        return phone || '';
    }
    // Find matching country code
    const matchingCountry = Object.entries(COUNTRY_DIAL_CODES).find(([_, dialCode]) => phone.startsWith(dialCode));
    if (!matchingCountry) {
        return phone;
    }
    const [_, dialCode] = matchingCountry;
    const nationalNumber = phone.substring(dialCode.length);
    if (format === 'national') {
        // Add leading zero for national format
        return `0${nationalNumber}`;
    }
    // International format with spacing
    return `${dialCode} ${nationalNumber}`;
}
